import { useState, useRef } from 'react';
import { createPortal } from 'react-dom';

const Tooltip = ({ text }) => {
  const [pos, setPos] = useState(null);
  const ref = useRef(null);
  const show = () => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    const above = r.top > 140;
    setPos({ left: r.left + r.width / 2, top: above ? r.top - 6 : r.bottom + 6, above });
  };
  return (
    <span ref={ref} className="inline-flex" onMouseEnter={show} onMouseLeave={() => setPos(null)}>
      <span className="w-3.5 h-3.5 rounded-full border border-gray-400 text-gray-400 text-[9px] flex items-center justify-center cursor-help select-none ml-1">i</span>
      {pos && createPortal(
        <span style={{ position: 'fixed', left: pos.left, top: pos.top, transform: pos.above ? 'translate(-50%, -100%)' : 'translate(-50%, 0)', zIndex: 9999, pointerEvents: 'none' }}
          className="px-2 py-1.5 text-xs bg-gray-800 text-white rounded shadow-lg w-52 text-left whitespace-normal">
          {text}
        </span>,
        document.body
      )}
    </span>
  );
};

const scoreColor = (score) => {
  if (score >= 0.9) return 'text-green-700 bg-green-50';
  if (score >= 0.7) return 'text-amber-700 bg-amber-50';
  return 'text-red-700 bg-red-50';
};

const directionBadge = (direction) => {
  const map = {
    conformant: 'bg-green-100 text-green-700',
    too_slow:   'bg-orange-100 text-orange-700',
    too_fast:   'bg-blue-100 text-blue-700',
    mixed:      'bg-purple-100 text-purple-700',
  };
  return map[direction] ?? 'bg-gray-100 text-gray-500';
};

const fmtSec = (s) => {
  if (s == null) return '—';
  if (s >= 86400) return `${(s / 86400).toFixed(1)}d`;
  if (s >= 3600)  return `${(s / 3600).toFixed(1)}h`;
  if (s >= 60)    return `${(s / 60).toFixed(1)}m`;
  return `${s.toFixed(0)}s`;
};

const KpiCard = ({ label, value, sub, tooltip }) => (
  <div className="flex flex-col gap-0.5 px-3 py-2 bg-gray-50 rounded-lg border border-gray-100">
    <span className="text-xs text-gray-500 flex items-center">
      {label}
      {tooltip && <Tooltip text={tooltip} />}
    </span>
    <span className="text-lg font-semibold text-gray-800">{value}</span>
    {sub && <span className="text-xs text-gray-400">{sub}</span>}
  </div>
);

const ThCell = ({ children, right, tooltip, className }) => (
  <th className={`px-3 py-2 ${right ? 'text-right' : 'text-left'}${className ? ` ${className}` : ''}`}>
    <span className="inline-flex items-center gap-0.5">
      {children}
      {tooltip && <Tooltip text={tooltip} />}
    </span>
  </th>
);

const TemporalResults = ({ results, onPairClick }) => {
  const {
    total_violations,
    total_checked_pairs,
    outlier_rate,
    profiling_mode,
    top_violating_pairs,
    trace_results,
  } = results;

  const unit = profiling_mode === 'robust' ? 'MAD' : 'σ';

  const violationRate = total_checked_pairs > 0
    ? ((total_violations / total_checked_pairs) * 100).toFixed(1)
    : '0.0';

  const displayPairs = [...top_violating_pairs]
    .sort((a, b) => b.violation_frequency - a.violation_frequency)
    .slice(0, 10);

  const displayTraces = [...trace_results]
    .sort((a, b) => a.temporal_score - b.temporal_score)  // worst first — analysts need to find violations
    .slice(0, 10);

  return (
    <div className="flex flex-col gap-4 overflow-auto">

      {/* Summary KPIs */}
      <div className="grid grid-cols-2 gap-2">
        <KpiCard
          label="Violation Rate"
          value={`${violationRate}%`}
          sub="of all checked transitions"
          tooltip="Percentage of transition pairs whose duration fell outside the expected time corridor, out of all pairs checked across all objects."
        />
        <KpiCard
          label="Affected Objects"
          value={`${outlier_rate.toFixed(1)}%`}
          sub="with ≥1 violation"
          tooltip="Percentage of object lifecycles that had at least one transition with a timing violation."
        />
      </div>

      {/* Top Violating Pairs */}
      {top_violating_pairs.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2 px-1">
            Top Violating Pairs
          </h4>
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="w-full text-xs table-fixed">
              <thead className="bg-gray-50 text-gray-500">
                <tr>
                  <ThCell className="w-[calc(50%_+_4px)]">Transition</ThCell>
                  <ThCell right tooltip="Share of objects where this transition violated the corridor, out of all objects that had this transition. The number in parentheses is the absolute violation count.">Viol. Rate</ThCell>
                  <ThCell right tooltip={`Median absolute deviation beyond the corridor boundary, expressed in ${unit === 'σ' ? 'standard deviations (σ)' : 'MAD units'}. Reflects the typical violation severity for this transition.`}>50%</ThCell>
                  <ThCell right tooltip={`95th-percentile absolute deviation beyond the corridor boundary, expressed in ${unit === 'σ' ? 'standard deviations (σ)' : 'MAD units'}. Reflects the severity of the worst violations for this transition.`}>95%</ThCell>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {displayPairs.map((pair, i) => (
                  <tr
                    key={i}
                    onClick={onPairClick ? (e) => onPairClick(`${pair.source_activity}→${pair.target_activity}`, e.clientX, e.clientY) : undefined}
                    className={`hover:bg-gray-50${onPairClick ? ' cursor-pointer' : ''}`}
                  >
                    <td className="px-3 py-2 font-medium text-gray-700 truncate">
                      {pair.source_activity} → {pair.target_activity}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-600 whitespace-nowrap">
                      <span className="text-red-600 font-medium">{(pair.violation_rate * 100).toFixed(1)}%</span>
                      <span className="text-gray-400 ml-1">({pair.violation_frequency})</span>
                    </td>
                    <td className="px-3 py-2 text-right text-gray-600">
                      {pair.p50_abs_deviation.toFixed(2)}×{unit}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-600">
                      {pair.p95_abs_deviation.toFixed(2)}×{unit}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Object Lifecycle Results */}
      <div>
        <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2 px-1">
          Object Lifecycle Results ({trace_results.length})
        </h4>
        <div className="overflow-x-auto rounded-lg border border-gray-200 max-h-64">
          <table className="w-full text-xs table-fixed">
            <thead className="bg-gray-50 text-gray-500 sticky top-0">
              <tr>
                <ThCell className="w-[calc(12.5%_+_1px)]">Object ID</ThCell>
                <ThCell className="w-[calc(12.5%_+_1px)]">Variant</ThCell>
                <ThCell right className="w-[calc(12.5%_+_1px)]" tooltip="Temporal conformance score for this object. 100% means all transitions were within the expected corridor.">Score</ThCell>
                <ThCell className="w-[calc(12.5%_+_1px)]" tooltip="Whether timing violations were predominantly too slow, too fast, or a mix of both.">Direction</ThCell>
                <ThCell className="w-[calc(50%_-_4px)]" tooltip="The transition with the largest absolute deviation from the corridor boundary for this object.">Critical Pair</ThCell>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {displayTraces.map((t, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="px-3 py-2 font-mono text-gray-600 max-w-[140px] truncate" title={t.object_id}>
                    {t.object_id}
                  </td>
                  <td className="px-3 py-2 text-gray-500">{t.variant_id}</td>
                  <td className="px-3 py-2 text-right">
                    <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-medium ${scoreColor(t.temporal_score)}`}>
                      {(t.temporal_score * 100).toFixed(0)}%
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    {t.direction ? (
                      <span className={`inline-block px-1.5 py-0.5 rounded text-xs ${directionBadge(t.direction)}`}>
                        {t.direction.replace('_', ' ')}
                      </span>
                    ) : '—'}
                  </td>
                  <td className="px-3 py-2 text-gray-500 max-w-[160px] truncate" title={t.critical_pair}>
                    {t.critical_pair ?? '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TemporalResults;
